import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BravosWordmark } from '../components/BravosWordmark';
import { usePageStatusBar } from '../hooks/usePageStatusBar';

const PAGE_TOP_DARK = 'var(--ink)';

/**
 * Hero video
 *
 * Envato Elements (and similar) don’t provide a hotlink URL — export your licensed MP4
 * (e.g. https://elements.envato.com/gym-battle-rope-and-man-with-exercise-hands-and-fi-MAHEBJA)
 * to `public/videos/landing-hero.mp4`, then set in `.env`:
 *   REACT_APP_LANDING_HERO_VIDEO=/videos/landing-hero.mp4
 *
 * Fallback: Mixkit — battle ropes at gym (free license, similar energy).
 * https://mixkit.co/free-stock-video/a-shirtless-young-man-swings-the-battle-ropes-with-vigorous-52088/
 */
const LANDING_HERO_VIDEO_FALLBACK =
  'https://assets.mixkit.co/videos/52088/52088-720.mp4';

const LANDING_HERO_VIDEO_POSTER =
  'https://assets.mixkit.co/videos/52088/52088-thumb-720-0.jpg';

function landingHeroVideoSrc(): string {
  const fromEnv = process.env.REACT_APP_LANDING_HERO_VIDEO?.trim();
  if (fromEnv) return fromEnv;
  return LANDING_HERO_VIDEO_FALLBACK;
}

export function LandingPage() {
  usePageStatusBar({ background: PAGE_TOP_DARK });

  const headlineRef = useRef<HTMLHeadingElement>(null);
  const [headlineWidthPx, setHeadlineWidthPx] = useState<number | null>(null);

  useEffect(() => {
    const el = headlineRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      setHeadlineWidthPx(Math.round(el.getBoundingClientRect().width));
    });
    ro.observe(el);
    setHeadlineWidthPx(Math.round(el.getBoundingClientRect().width));
    return () => ro.disconnect();
  }, []);

  const videoSrc = landingHeroVideoSrc();

  const ctaClass =
    'inline-flex min-h-[2.5rem] w-full min-w-0 items-center justify-center rounded-xl border border-white/20 bg-white px-2 py-2 text-xs font-bold text-[var(--ink)] shadow-[0_8px_24px_rgba(0,0,0,0.2)] transition hover:bg-[var(--border)] active:bg-[#d4d4d4] sm:min-h-[2.75rem] sm:px-3 sm:text-sm';

  return (
    <div className="relative min-h-screen font-bravos antialiased">
      <div className="pointer-events-none fixed inset-0 z-0" aria-hidden>
        <video
          className="h-full w-full object-cover object-center"
          autoPlay
          muted
          loop
          playsInline
          poster={LANDING_HERO_VIDEO_POSTER}
          preload="auto"
        >
          <source src={videoSrc} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/10 via-transparent to-black/45" />
      </div>

      <div className="relative z-10 flex min-h-screen flex-col text-white">
        <header
          className="relative z-50 w-full shrink-0"
          style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
        >
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6 sm:py-4">
            <Link to="/" className="shrink-0" aria-label="Bravos home">
              <BravosWordmark className="h-10 w-auto text-paper sm:h-12" />
            </Link>
            <Link
              to="/auth?mode=login"
              className="shrink-0 rounded-xl bg-[var(--action-primary)] px-[18px] py-2.5 text-center text-xs font-bold leading-normal text-[var(--ink)] shadow-[0_4px_14px_rgba(0,0,0,0.25)] transition hover:bg-[var(--action-primary-pressed)] active:bg-[var(--success)] sm:text-sm"
            >
              Sign in
            </Link>
          </div>
        </header>

        <main className="flex min-h-0 flex-1 flex-col">
          <section
            className="flex flex-1 flex-col items-center justify-center px-4 py-12 pb-20 sm:px-6 sm:py-16 sm:pb-24 lg:min-h-0 lg:pb-28"
            aria-labelledby="landing-hero-heading"
          >
            <div className="mx-auto flex w-full max-w-5xl flex-col items-center text-center">
              <h1
                ref={headlineRef}
                id="landing-hero-heading"
                className="font-pageTitle text-center text-[clamp(1.25rem,5.25vw+0.35rem,3.875rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-white whitespace-nowrap"
              >
                Fitness that fits your life
              </h1>
              <p
                className="mx-auto mt-4 text-center text-sm font-semibold leading-snug tracking-wide text-white/85 sm:mt-5 sm:text-base md:text-lg"
                style={headlineWidthPx != null ? { maxWidth: headlineWidthPx } : undefined}
              >
                Your plan. Your coach. One place.
              </p>
              <div
                className="mx-auto mt-8 grid w-full max-w-full grid-cols-2 gap-2 sm:mt-10 sm:gap-3 lg:mt-12"
                style={headlineWidthPx != null ? { maxWidth: headlineWidthPx } : undefined}
              >
                <Link to="/auth?mode=signup&role=member" className={ctaClass}>
                  Start Training
                </Link>
                <Link to="/auth?mode=signup&role=trainer" className={ctaClass}>
                  Coach Clients
                </Link>
              </div>
            </div>
          </section>
        </main>

        {/*
          In-flow footer (not position:fixed): #root applies horizontal safe-area padding, so a
          viewport-fixed bar was centering in the full window while the header centers in the
          inset content box — edges looked off on some desktops and notched layouts.
        */}
        <footer
          className="relative z-40 shrink-0"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          <div className="mx-auto flex max-w-7xl items-end justify-between gap-4 px-4 pb-3 pt-2 sm:px-6 sm:pb-4 sm:pt-2">
            <nav
              className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 text-left text-xs font-normal text-white/80 drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)] sm:gap-x-5 sm:text-sm"
              aria-label="Legal"
            >
              <Link to="/terms" className="transition hover:text-white">
                T&Cs
              </Link>
              <Link to="/privacy" className="transition hover:text-white">
                Privacy Policy
              </Link>
            </nav>
            <p className="pointer-events-none shrink-0 text-right text-xs font-normal text-white/80 drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)] sm:text-sm">
              © 2026 Bravos
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default LandingPage;
